<template>
  <v-dialog v-model="dialog" width="700px" scrollable persistent>
    <template v-slot:activator="{ on }">
      <v-text-field outlined hide-details :dense="dense" :value="textFieldValue" v-on="on" />
    </template>

    <v-card>
      <v-card-title>Selecione um período</v-card-title>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row>
            <v-col>
              <v-select v-model="selectedPeriod" label="Opções" hide-details solo :items="selectOptions" @input="selectDates" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <mf-date-range-picker
                label="Data inicial - Data final"
                :initial-date.sync="initialDate"
                :final-date.sync="finalDate"
                :min="firstDay"
                :max="lastDay"
                :error-messages="rangeError"
                @input="setSelectedPeriodAsCustom"
              />
            </v-col>
          </v-row>
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: 'Confirmar',
            action: submit,
            isVisible: true,
            isDisabled: Boolean(rangeError),
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    value: [Array, String],
    defaultDate: {
      type: String,
      default: 'mesMaisRecente',
      validator: (value) =>
        ['diaMaisRecente', 'diaAnterior', 'ultimosSeteDias', 'ultimosTrintaDias', 'mesMaisRecente', 'mesAnterior', 'desdeInicio'].indexOf(value) !== -1,
    },
    maxFilterDays: {
      type: Number,
      default: null,
    },
    useCurrentDateAsLastDay: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: false,
    selectedPeriod: '',
    initialDate: '',
    finalDate: '',
    rangeError: '',
  }),

  computed: {
    ...mapGetters({
      primeiroRegistro: 'empresaPrimeiroRegistro',
    }),
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    textFieldValue() {
      switch (this.selectedPeriod) {
        case 'diaMaisRecente':
          return 'Dia mais recente'
        case 'diaAnterior':
          return 'Dia anterior'
        case 'ultimosSeteDias':
          return 'Últimos 7 dias'
        case 'ultimosTrintaDias':
          return 'Últimos 30 dias'
        case 'mesMaisRecente':
          return 'Mês mais recente'
        case 'mesAnterior':
          return 'Mês anterior'
        case 'desdeInicio':
          return 'Desde o início'
        case 'periodoPersonalizado':
          const formattedInitialDate = DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          const formattedFinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          return `${formattedInitialDate} - ${formattedFinalDate}`
        default:
          return ''
      }
    },

    selectOptions() {
      return [
        {
          text: 'Dia mais recente',
          value: 'diaMaisRecente',
        },
        {
          text: 'Dia anterior',
          value: 'diaAnterior',
        },
        {
          text: 'Últimos 7 dias',
          value: 'ultimosSeteDias',
        },
        {
          text: 'Últimos 30 dias',
          value: 'ultimosTrintaDias',
        },
        {
          text: 'Mês mais recente',
          value: 'mesMaisRecente',
        },
        {
          text: 'Mês anterior',
          value: 'mesAnterior',
        },
        ...(!this.maxFilterDays ? [{ text: 'Desde o início', value: 'desdeInicio' }] : []),
        {
          text: 'Período personalizado',
          value: 'periodoPersonalizado',
        },
      ]
    },

    firstDay() {
      if (this.primeiroRegistro) return DateTime.fromFormat(this.primeiroRegistro.toString(), 'yyyy-MM-dd HH:mm:ss').toFormat('yyyy-MM-dd')
      return ''
    },
    lastDay() {
      if (this.useCurrentDateAsLastDay) return DateTime.now().toFormat('yyyy-MM-dd')
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    yesterday() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ days: -1 }).toFormat('yyyy-MM-dd')
    },
    sevenDaysAgo() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ days: -6 }).toFormat('yyyy-MM-dd')
    },
    thirtyDaysAgo() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ days: -29 }).toFormat('yyyy-MM-dd')
    },
    firstDayOfCurrentMonth() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').startOf('month').toFormat('yyyy-MM-dd')
    },
    firstDayOfLastMonth() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ months: -1 }).startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDayOfLastMonth() {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ month: -1 }).endOf('month').toFormat('yyyy-MM-dd')
    },
    selectedOption: {
      get() {
        return this.selected
      },
      set(value) {
        this.$emit('update:selected', value)
      },
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        if (!this.value) {
          this.selectDates(this.defaultDate)
        }
      }
    },
    value(value) {
      if (!value) {
        this.selectedPeriod = this.defaultDate
      }
    },
    initialDate(value) {
      if (value) {
        const InitialDate = DateTime.fromFormat(value[0], 'yyyy-MM-dd')
        const FinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd')
        if (InitialDate > FinalDate) {
          this.finalDate = InitialDate.toFormat('yyyy-MM-dd')
        }
      }
    },
    finalDate(value) {
      if (value) {
        const FinalDate = DateTime.fromFormat(value[1], 'yyyy-MM-dd')
        const InitialDate = this.initialDate ? DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd') : DateTime.now().startOf('year')
        if (FinalDate < InitialDate) {
          this.initialDate = FinalDate.toFormat('yyyy-MM-dd')
        }
      }
    },
  },
  mounted() {
    this.selectedPeriod = this.defaultDate
    this.selectDates(this.defaultDate)
  },
  methods: {
    checkError() {
      if (!this.maxFilterDays) {
        this.rangeError = ''
        return
      }
      const initialDate = DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd')
      const finalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd')
      if (finalDate.diff(initialDate, ['days']).days > this.maxFilterDays) {
        this.rangeError = `O período selecionado deve ter no máximo ${this.maxFilterDays} dias.`
      } else {
        this.rangeError = ''
      }
    },
    submit() {
      this.$emit('input', [this.initialDate, this.finalDate])
      this.dialog = false
    },
    selectDates(value) {
      switch (value) {
        case 'diaMaisRecente':
          this.setInitialAndFinalDate({ initial: this.lastDay, final: this.lastDay })
          break
        case 'diaAnterior':
          this.setInitialAndFinalDate({ initial: this.yesterday, final: this.yesterday })
          break
        case 'ultimosSeteDias':
          this.setInitialAndFinalDate({ initial: this.sevenDaysAgo, final: this.lastDay })
          break
        case 'ultimosTrintaDias':
          this.setInitialAndFinalDate({ initial: this.thirtyDaysAgo, final: this.lastDay })
          break
        case 'mesMaisRecente':
          this.setInitialAndFinalDate({ initial: this.firstDayOfCurrentMonth, final: this.lastDay })
          break
        case 'mesAnterior':
          this.setInitialAndFinalDate({ initial: this.firstDayOfLastMonth, final: this.lastDayOfLastMonth })
          break
        case 'desdeInicio':
          this.setInitialAndFinalDate({ initial: this.firstDay, final: this.lastDay })
          break
        case 'periodoPersonalizado':
          this.setInitialAndFinalDate({ initial: this.lastDay, final: this.lastDay })
          break
        default:
          break
      }
    },
    setInitialAndFinalDate({ initial, final }) {
      this.initialDate = initial
      this.finalDate = final
    },
    setDefaultSelectedPeriod() {
      this.selectedPeriod = this.defaultDate
    },
    setSelectedPeriodAsCustom() {
      this.checkError()
      const customPeriodValue = 'periodoPersonalizado'
      this.selectedPeriod = customPeriodValue
    },
  },
}
</script>
<style lang="scss">
.data-select {
  .v-text-field__details {
    margin: 0 !important;
  }
}

.date-card {
  user-select: none;
  cursor: pointer;
}
</style>
